import { clsx } from 'clsx';
import type { ReactNode } from 'react';

interface BadgeProps {
    color:
        | 'gray'
        | 'red'
        | 'yellow'
        | 'green'
        | 'blue'
        | 'teal'
        | 'indigo'
        | 'purple'
        | 'pink'
        | 'emerald'
        | 'violet'
        | 'lime'
        | 'fuchsia'
        | 'orange'
        | 'white'
        | 'amber';
    style?: 'default' | 'shadow' | 'square';
    text: string;
    icon?: ReactNode;
    className?: string;
    onClick?: () => void;
}
const Badge = ({ color, text, icon, style = 'default', className, onClick }: BadgeProps) => (
    <span
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
        onClick={onClick}
        onKeyDown={
            onClick
                ? e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onClick();
                      }
                  }
                : undefined
        }
        className={clsx(
            style == 'shadow' &&
                'inline-flex items-center gap-1.5 rounded-[30px] px-2.5 py-1.5 text-base leading-normal font-semibold shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] ring-1 ring-inset',
            style == 'default' &&
                'font-500 font-large inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset',
            style == 'square' &&
                'inline-flex items-center justify-start rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset',
            onClick &&
                'cursor-pointer select-none focus-visible:ring-2 focus-visible:ring-neutral-300 focus-visible:ring-offset-2 focus-visible:outline-none',
            color === 'gray' && 'bg-gray-50 text-gray-400 ring-gray-400/10',
            color === 'red' && 'bg-red-50 text-red-700 ring-red-600/10',
            color === 'yellow' && 'bg-yellow-50 text-yellow-800 ring-yellow-600/20',
            color === 'green' && 'bg-green-50 text-green-500 ring-green-500',
            color === 'blue' && 'bg-blue-50 text-blue-500 ring-blue-500/10',
            color === 'teal' && 'bg-teal-50 text-teal-600 ring-teal-200',
            color === 'indigo' && 'bg-indigo-50 text-indigo-700 ring-indigo-700/10',
            color === 'purple' && 'bg-purple-50 text-purple-500 ring-purple-500/10',
            color === 'pink' && 'bg-pink-50 text-pink-500 ring-pink-700/10',
            color === 'emerald' && 'bg-emerald-50 text-emerald-500 ring-emerald-500/10',
            color === 'violet' && 'bg-violet-50 text-violet-500 ring-violet-700/10',
            color === 'lime' && 'bg-lime-50 text-lime-600 ring-lime-700/10',
            color === 'fuchsia' && 'bg-fuchsia-50 text-fuchsia-500 ring-fuchsia-500/10',
            color === 'orange' && 'bg-orange-50 text-orange-500 ring-orange-500/10',
            color === 'white' && 'bg-white-50 text-black-400 ring-gray-400/10',
            color === 'amber' && 'bg-amber-50 text-amber-400 ring-amber-400/10',
            className
        )}
    >
        {icon && <span>{icon}</span>}
        {text}
    </span>
);

export default Badge;
